import { useState, type FormEvent } from 'react';
import { api, bodyOf, type Plan, type RuleVersion } from '../lib/api.ts';
import { breakName, describeRule, shortDate, type Names } from '../lib/describe.ts';
import { today } from '../lib/plantime.ts';
import { ErrorNote, Field, Icon, Sheet, useAction, useLoad } from '../ui.tsx';
import { RuleForm } from './RuleForm.tsx';

const GROUPS: { title: string; types: RuleVersion['type'][] }[] = [
  { title: 'Regular schedule', types: ['rotation', 'midweek'] },
  { title: 'Holidays and breaks', types: ['schoolBreak', 'holiday', 'specialDay'] },
  { title: 'Dated blocks', types: ['block'] },
];

/** The latest version of each rule, with its full history. */
function byRule(versions: RuleVersion[]) {
  const map = new Map<string, RuleVersion[]>();
  for (const v of versions) map.set(v.ruleId, [...(map.get(v.ruleId) ?? []), v]);
  return [...map.values()].map((vs) => {
    vs.sort((a, b) => a.version - b.version);
    const latest = vs[vs.length - 1]!;
    const live = [...vs].reverse().find((v) => !v.ended) ?? vs[0]!;
    return { history: vs, latest, live };
  });
}

export function Schedule({ plan, names, onPlanChanged }: { plan: Plan; names: Names; onPlanChanged: () => void }) {
  const rules = useLoad(() => api.rules(plan.id), [plan.id]);
  const [sheet, setSheet] = useState<{ kind: 'add' } | { kind: 'change' | 'end'; rule: RuleVersion } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const done = () => {
    setSheet(null);
    rules.reload();
  };
  const list = rules.data ? byRule(rules.data) : [];

  return (
    <>
      <div className="title-row">
        <div>
          <h1>Schedule</h1>
          <p className="finding">
            The parenting plan, clause by clause. Every figure in Kept is worked out from these rules. A change takes effect from a date you choose, so
            earlier months never move.
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setSheet({ kind: 'add' })}>
            <Icon name="plus" />
            Add a rule
          </button>
        </div>
      </div>

      <div className="cols">
        <section className="panel">
          <div className="panel-head">
            <h2>Rules</h2>
            <span className="meta">{list.filter((r) => !r.latest.ended).length} in effect</span>
          </div>
          <ErrorNote error={rules.error} />
          {rules.loading && !rules.data ? <p className="empty">Loading…</p> : null}
          {rules.data && list.length === 0 ? (
            <p className="empty">No rules yet. Start with the regular schedule, then holidays and breaks.</p>
          ) : null}
          {GROUPS.map((g) => {
            const items = list.filter((r) => g.types.includes(r.latest.type));
            if (!items.length) return null;
            return (
              <div key={g.title}>
                <p className="group-label">{g.title}</p>
                <div className="rows">
                  {items.map(({ history, latest, live }) => (
                    <div className="row" key={latest.ruleId}>
                      <p className="row-text" style={latest.ended ? { color: 'var(--muted)' } : undefined}>
                        {describeRule(bodyOf(live), names)}
                      </p>
                      <div className="row-meta">
                        <span>{live.clause}</span>
                        <span>
                          {latest.ended ? `Ended ${shortDate(latest.effectiveFrom)}` : `In effect from ${shortDate(live.effectiveFrom)}`}
                        </span>
                        {history.length > 1 && (
                          <button className="link" onClick={() => setOpen(open === latest.ruleId ? null : latest.ruleId)}>
                            {history.length} versions
                          </button>
                        )}
                        {live.tier != null && <span>Priority {live.tier}</span>}
                      </div>
                      {!latest.ended && (
                        <div className="row-actions">
                          <button className="btn btn-ghost" onClick={() => setSheet({ kind: 'change', rule: latest })}>
                            Change
                          </button>
                          <button className="btn btn-ghost" onClick={() => setSheet({ kind: 'end', rule: latest })}>
                            End
                          </button>
                        </div>
                      )}
                      {open === latest.ruleId && (
                        <ol className="row-meta" style={{ gridColumn: '1 / -1', display: 'grid', gap: 4, paddingLeft: 18, margin: '6px 0 0' }}>
                          {history.map((v) => (
                            <li key={v.id}>
                              From {shortDate(v.effectiveFrom)}: {v.ended ? 'ended' : describeRule(bodyOf(v), names)}
                              {v.reason ? ` (${v.reason})` : ''}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <SchoolBreaks plan={plan} onChanged={onPlanChanged} />
      </div>

      {sheet?.kind === 'add' && (
        <Sheet title="Add a rule" onClose={() => setSheet(null)}>
          <RuleForm plan={plan} names={names} onSaved={done} />
        </Sheet>
      )}
      {sheet?.kind === 'change' && (
        <Sheet title="Change this rule" onClose={() => setSheet(null)}>
          <RuleForm plan={plan} names={names} current={sheet.rule} onSaved={done} />
        </Sheet>
      )}
      {sheet?.kind === 'end' && (
        <Sheet title="End this rule" onClose={() => setSheet(null)}>
          <EndRule plan={plan} rule={sheet.rule} names={names} onDone={done} />
        </Sheet>
      )}
    </>
  );
}

function EndRule({ plan, rule, names, onDone }: { plan: Plan; rule: RuleVersion; names: Names; onDone: () => void }) {
  const [date, setDate] = useState(today(plan.timeZone));
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.addRuleVersion(plan.id, rule.ruleId, { effectiveFrom: date, ended: true, reason: reason.trim() });
      onDone();
    });
  };
  return (
    <form className="form" onSubmit={submit}>
      <div className="preview">{describeRule(bodyOf(rule), names)}</div>
      <p className="muted">The rule stops applying from this date. Dates before it keep it, and its history stays.</p>
      <Field label="Last applies before" hint={`After ${rule.effectiveFrom}`}>
        <input className="input" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label="Why it ended">
        <input className="input" required value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ErrorNote error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy}>
          End rule
        </button>
      </div>
    </form>
  );
}

function SchoolBreaks({ plan, onChanged }: { plan: Plan; onChanged: () => void }) {
  const [form, setForm] = useState({ kind: 'spring', lastSchoolDay: '', firstDayBack: '' });
  const add = useAction();
  const remove = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void add.run(async () => {
      await api.addBreak(plan.id, form);
      setForm({ ...form, lastSchoolDay: '', firstDayBack: '' });
      onChanged();
    });
  };
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>School breaks</h2>
        <span className="meta">From the school calendar</span>
      </div>
      <div className="rows">
        {plan.schoolBreaks.length === 0 && <p className="empty">None yet. Break rules apply only to the breaks listed here.</p>}
        {plan.schoolBreaks.map((b) => (
          <div className="row" key={b.id}>
            <p className="row-text">{breakName(b.kind)}</p>
            <p className="row-meta">
              Last school day {shortDate(b.lastSchoolDay)} · back {shortDate(b.firstDayBack)}
            </p>
            <div className="row-actions">
              <button
                className="btn btn-ghost"
                aria-label={`Remove ${breakName(b.kind)} ${b.lastSchoolDay}`}
                disabled={remove.busy}
                onClick={() =>
                  void remove.run(async () => {
                    await api.deleteBreak(plan.id, b.id);
                    onChanged();
                  })
                }
              >
                <Icon name="close" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <ErrorNote error={remove.error} />
      <form className="form" onSubmit={submit} style={{ marginTop: 16 }}>
        <Field label="Break">
          <select className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {['spring', 'thanksgiving', 'winter', 'fall'].map((k) => (
              <option key={k} value={k}>
                {breakName(k)}
              </option>
            ))}
          </select>
        </Field>
        <div className="two">
          <Field label="Last school day">
            <input className="input" type="date" required value={form.lastSchoolDay} onChange={(e) => setForm({ ...form, lastSchoolDay: e.target.value })} />
          </Field>
          <Field label="First day back">
            <input className="input" type="date" required value={form.firstDayBack} onChange={(e) => setForm({ ...form, firstDayBack: e.target.value })} />
          </Field>
        </div>
        <ErrorNote error={add.error} />
        <button className="btn" disabled={add.busy}>
          Add break
        </button>
      </form>
    </section>
  );
}
